// El REGISTRO del puente entre el chat y la pantalla —
// ADR-2026-09-10-el-agente-toca-la-pantalla.
//
// Vive arriba del árbol (lo monta `EntornoChatAtlas`, por encima de las rutas y del
// chat) y guarda el controlador de la pantalla que está montada. No es estado de React
// a propósito: la pantalla se describe en CADA render (sus valores cambian con cada
// click) y el chat sólo la lee cuando el modelo llama una herramienta. Con `useState`
// acá, cada cambio de un campo del modo re-renderizaría a todo el que lea el contexto.
// Con un ref, registrar es gratis y leer es `actual()` en el momento justo.
//
// Una pantalla se registra con `useRegistrarPantalla(controlador)` en cada render; el
// hook guarda el último controlador en su propio ref y registra UNA vez un lector de ese
// ref, así el registro no se rehace nunca y el chat siempre ve la descripción fresca.
// `null` mientras la pantalla no tiene nada que ofrecer (el modo cerrado, cargando).

import React, { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { ControladorDePantalla } from './puenteDePantalla';

type Lector = () => ControladorDePantalla | null;

export interface PuenteDePantalla {
  /** El controlador de la pantalla montada, o null si no hay ninguna con campos. */
  actual: () => ControladorDePantalla | null;
  /** Registra un lector; devuelve la función para darlo de baja. */
  registrar: (lector: Lector) => () => void;
}

const SIN_PUENTE: PuenteDePantalla = {
  actual: () => null,
  registrar: () => () => {},
};

const PuenteContext = createContext<PuenteDePantalla>(SIN_PUENTE);

export const PuenteDePantallaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Una pila y no un slot: si dos pantallas conviven un instante (la que se va y la que
  // llega en una navegación), gana la última que se registró y siga viva.
  const lectoresRef = useRef<Lector[]>([]);
  const valor = useMemo<PuenteDePantalla>(
    () => ({
      actual: () => {
        const lectores = lectoresRef.current;
        for (let i = lectores.length - 1; i >= 0; i--) {
          const c = lectores[i]();
          if (c) return c;
        }
        return null;
      },
      registrar: (lector) => {
        lectoresRef.current = [...lectoresRef.current, lector];
        return () => {
          lectoresRef.current = lectoresRef.current.filter((l) => l !== lector);
        };
      },
    }),
    [],
  );
  return <PuenteContext.Provider value={valor}>{children}</PuenteContext.Provider>;
};

/** Lo lee el chat para resolver `ver_pantalla`, `configurar_pantalla` y `abrir_modo`. */
export const usePuenteDePantalla = (): PuenteDePantalla => useContext(PuenteContext);

/**
 * Lo llama una pantalla con campos, en cada render, con su controlador vigente (o null
 * si en este momento no tiene nada que ofrecer). Al desmontarse se da de baja sola.
 */
export function useRegistrarPantalla(controlador: ControladorDePantalla | null): void {
  const { registrar } = useContext(PuenteContext);
  const ref = useRef<ControladorDePantalla | null>(controlador);
  ref.current = controlador;
  useEffect(() => registrar(() => ref.current), [registrar]);
}
